import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { LandingCtaLink } from '@/landing/landing-cta-link'
import { cn } from '@/lib/utils'

type MarketingButtonVariant = 'primary' | 'light' | 'gold'

const variantClass: Record<MarketingButtonVariant, string> = {
  primary:
    'bg-[var(--marketing-button-primary-bg,var(--marketing-text,#1f2528))] text-[var(--marketing-button-primary-fg,#ffffff)] shadow-[0_12px_30px_rgba(31,37,40,0.15)] hover:opacity-90',
  light:
    'border border-[var(--marketing-border,#e9e3d6)] bg-[var(--marketing-card,#ffffff)] text-[var(--marketing-text,#1f2528)] shadow-none hover:opacity-90',
  gold: 'border-0 bg-[var(--marketing-gold,#f6c94c)] text-[var(--marketing-button-primary-fg,#1f2528)] shadow-none hover:bg-[var(--marketing-gold,#f6c94c)]/90',
}

type MarketingButtonProps = {
  variant?: MarketingButtonVariant
  href?: string
  children: ReactNode
  className?: string
  type?: 'button' | 'submit'
  onClick?: ComponentPropsWithoutRef<'button'>['onClick']
  disabled?: boolean
}

export function MarketingButton({
  variant = 'primary',
  href,
  children,
  className,
  type = 'button',
  onClick,
  disabled,
}: MarketingButtonProps) {
  const base = cn(
    'inline-flex items-center justify-center rounded-full px-[22px] py-3.5 text-[15px] font-semibold transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--marketing-gold,#f6c94c)] focus-visible:ring-offset-2',
    variantClass[variant],
    disabled && 'pointer-events-none opacity-60',
    className
  )

  if (href) {
    return (
      <LandingCtaLink to={href} className={base}>
        {children}
      </LandingCtaLink>
    )
  }

  return (
    <button type={type} className={base} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  )
}
